import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useToast } from '@retail/hooks';
import {
  useCreateOrUpdateSupplierItemAssortmentTags,
  useDeleteSupplierItemAssortmentTag,
} from '@retail/products/data-access';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AssortmentActionOptions } from './useAssortmentItemsActions';

export interface AssortmentTagItemsUpdate {
  tagId: number;
  supplierItemIds: number[];
  validFrom?: string;
  validTo?: string;
}

export interface AssortmentTagItemsRemoval {
  tagId: number;
  supplierItemIds: number[];
}

export interface AssortmentTagChanges {
  updates: AssortmentTagItemsUpdate[];
  removals: AssortmentTagItemsRemoval[];
}

export const useAssortmentTagActions = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const t = useAppTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const { mutateAsync: createOrUpdateTagItems, isPending: createOrUpdateIsPending } =
    useCreateOrUpdateSupplierItemAssortmentTags();
  const { mutateAsync: deleteTagItem, isPending: deleteIsPending } =
    useDeleteSupplierItemAssortmentTag();

  const updateAssortmentTags = useCallback(
    async (
      { updates, removals }: AssortmentTagChanges,
      { skipToast }: AssortmentActionOptions = {}
    ) => {
      try {
        await Promise.all([
          ...updates.map(({ tagId, supplierItemIds, validFrom, validTo }) =>
            createOrUpdateTagItems({
              urlVariables: { tagId, assortmentId },
              body: { supplierItemIds, validFrom, validTo },
            })
          ),
          ...removals.flatMap(({ tagId, supplierItemIds }) =>
            supplierItemIds.map((supplierItemId) =>
              deleteTagItem({ urlVariables: { tagId, assortmentId, supplierItemId } })
            )
          ),
        ]);

        if (!skipToast) toast.success(t('myAssortment.actions.assortmentUpdated'));
        queryClient.invalidateQueries({ queryKey: ['mg-item'] });
        queryClient.invalidateQueries({ queryKey: ['mg-item-assortment-tags'] });
        queryClient.invalidateQueries({ queryKey: ['assortmentItems'] });
        queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
        queryClient.invalidateQueries({ queryKey: ['assortment-prices'] });
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [assortmentId, createOrUpdateTagItems, deleteTagItem, queryClient, t, toast]
  );

  return {
    updateAssortmentTags,
    updateAssortmentTagsIsLoading: createOrUpdateIsPending || deleteIsPending,
  };
};
